import { Amenity, Department, NavPoint, OfficeUtilityType, Person, RoomType, Team } from "@office-map/api-schema";

import { orgRepository } from "@/features/storage/repositories/org.repository";
import { physicalRepository } from "@/features/storage/repositories/physical.repository";
import { MapRoom, SearchableEmployee, DepartmentNode, EmployeeNode, FloorNode } from "@/features/storage/storage.types";

class StorageService {
    private orgRepository = orgRepository;
    private physicalRepository = physicalRepository;

    private subscriptions: Array<(changes: unknown) => void> = [];

    /**
     * Subscribe to all changes in the storage service, so React components be notified to re-render.
     */
    subscribe(callback: (changes: unknown) => void): () => void {
        this.subscriptions.push(callback);

        return () => {
            this.subscriptions = this.subscriptions.filter((cb) => cb !== callback);
        };
    }

    notifySubscribers(changes: unknown) {
        this.subscriptions.forEach((callback) => callback(changes));
    }

    getSearchableEmployees(): SearchableEmployee[] {
        return this.orgRepository.findAllEmployees().map((employee) => {
            const seatDetails = this.physicalRepository.getSeatDetailsByEmployeeId(employee.id);
            return {
                id: employee.id,
                name: employee.name,
                department: employee.department,
                team: employee.team,
                floor: seatDetails ? parseInt(seatDetails.floorName.replace("Floor ", "")) : -1,
                room: seatDetails ? seatDetails.roomName : "Unknown room",
                seat: seatDetails ? parseInt(seatDetails.id.replace("seat-", "")) : -1,
            };
        });
    }

    getFloorMap(floorId: string): MapRoom[] {
        const floorRooms = this.physicalRepository.getFloorRooms(floorId);
        if (floorRooms === null) return [];

        return floorRooms.map((room) => ({
            ...room,
            origin: {
                x: room.shape.origin[0],
                y: room.shape.origin[1],
            },
            polygonPoints: room.shape.points.map((point) => ({ x: point[0], y: point[1] })),
            seats:
                this.physicalRepository.getRoomSeats(room.id)?.map((seat) => ({
                    id: seat.id,
                    roomPosition: { x: seat.location.x, y: seat.location.y, theta: seat.theta },
                    occupiedBy: seat.occupantId !== null ? this.orgRepository.findEmployeeById(seat.occupantId) : null,
                })) ?? [],
        }));
    }

    getRooms(): MapRoom[] {
        const floorRooms = this.physicalRepository.getALlRooms();
        if (floorRooms === null) return [];

        return floorRooms.map((room) => ({
            ...room,
            origin: {
                x: room.shape.origin[0],
                y: room.shape.origin[1],
            },
            polygonPoints: room.shape.points.map((point) => ({ x: point[0], y: point[1] })),
            seats:
                this.physicalRepository.getRoomSeats(room.id)?.map((seat) => ({
                    id: seat.id,
                    roomPosition: { x: seat.location.x, y: seat.location.y, theta: seat.theta },
                    occupiedBy: seat.occupantId !== null ? this.orgRepository.findEmployeeById(seat.occupantId) : null,
                })) ?? [],
        }));
    }

    getFloorIdByRoomId(roomId: string): string | null {
        const floor = this.physicalRepository.getFloorByRoomId().get(roomId)?.[0];
        return floor ? floor.floorId : null;
    }

    getFloorsAndRoomsTree(): FloorNode[] {
        const floor3Rooms = this.physicalRepository.getFloorRooms("floor-3");
        const floor4Rooms = this.physicalRepository.getFloorRooms("floor-4");
        const floor5Rooms = this.physicalRepository.getFloorRooms("floor-5");
        const floor6Rooms = this.physicalRepository.getFloorRooms("floor-6");

        return [
            { id: "3", name: "Floor 3", children: floor3Rooms ?? [] },
            { id: "4", name: "Floor 4", children: floor4Rooms ?? [] },
            { id: "5", name: "Floor 5", children: floor5Rooms ?? [] },
            { id: "6", name: "Floor 6", children: floor6Rooms ?? [] },
        ].map((floor) => ({
            ...floor,
            children: floor.children.map((room) => ({
                ...room,
                origin: {
                    x: room.shape.origin[0],
                    y: room.shape.origin[1],
                },
                polygonPoints: room.shape.points.map((point) => ({ x: point[0], y: point[1] })),
                seats:
                    this.physicalRepository.getRoomSeats(room.id)?.map((seat) => ({
                        id: seat.id,
                        roomPosition: { x: seat.location.x, y: seat.location.y },
                        occupiedBy:
                            seat.occupantId !== null ? this.orgRepository.findEmployeeById(seat.occupantId) : null,
                    })) ?? [],
            })),
        }));
    }

    getOrganizationTree(): (DepartmentNode | EmployeeNode)[] {
        const departments = this.orgRepository.findAllDepartments();
        const teams = this.orgRepository.findAllTeams();
        const people = this.orgRepository.findAllEmployees();

        const toEmployeeNode = (member: (typeof people)[number]): EmployeeNode => {
            const seatDetails = this.physicalRepository.getSeatDetailsByEmployeeId(member.id);
            return {
                id: member.id,
                name: member.name,
                type: "employee",
                floor: seatDetails ? parseInt(seatDetails.floorId.replace("floor-", "")) : -1,
                room: seatDetails ? seatDetails.roomName : "Unknown room",
                seat: seatDetails ? parseInt(seatDetails.id.replace("seat-", "")) : -1,
                avatar: member.avatar,
                personSource: member,
            };
        };

        const assignedPersonIds = new Set([
            ...teams.flatMap((team) => team.memberIds),
            ...departments.flatMap((department) => department.personIds ?? []),
        ]);

        const departmentNodes: DepartmentNode[] = departments.map(
            (department): DepartmentNode => ({
                ...department,
                type: "department",
                departmentSource: department,
                children: [
                    ...department.teamIds
                        .map((teamId) => teams.find((team) => team.id === teamId))
                        .filter((team) => team !== undefined)
                        .map((team) => ({
                            ...team,
                            type: "team" as const,
                            teamSource: team,
                            children: team.memberIds
                                .map((memberId) => people.find((person) => person.id === memberId))
                                .filter((member) => member !== undefined)
                                .map(toEmployeeNode),
                        })),
                    ...(department.personIds ?? [])
                        .map((personId) => people.find((person) => person.id === personId))
                        .filter((member) => member !== undefined)
                        .map(toEmployeeNode),
                ],
            }),
        );

        const unassignedNodes: EmployeeNode[] = people
            .filter((person) => !assignedPersonIds.has(person.id))
            .map(toEmployeeNode);

        return [...departmentNodes, ...unassignedNodes];
    }

    getEmployees() {
        return this.orgRepository.findAllEmployees().map((employee) => {
            const seatDetails = this.physicalRepository.getSeatDetailsByEmployeeId(employee.id);
            return {
                ...employee,
                seat: seatDetails?.id || "Unassigned",
            };
        });
    }

    getDepartments() {
        return this.orgRepository.findAllDepartments();
    }

    getTeams() {
        return this.orgRepository.findAllTeams();
    }

    getSeatById(seatId: string) {
        return this.physicalRepository.getSeatDetailsById(seatId);
    }

    addSeat(roomId: string): string | null {
        const value = this.physicalRepository.addSeatToRoom(roomId, { x: 0, y: 0 });

        this.notifySubscribers({ type: "seat-added", roomId, seatId: value });

        return value;
    }

    changeRoomName(roomId: string, newName: string): void {
        const value = this.physicalRepository.renameRoom(roomId, newName);
        this.notifySubscribers({ type: "room-renamed", roomId, newName: value });
        return value;
    }

    moveSeat(roomId: string, seatId: string, { x, y }: { x: number; y: number }) {
        const value = this.physicalRepository.updateSeatLocation(roomId, seatId, { x, y });
        this.notifySubscribers({ type: "seat-moved", roomId, seatId, newPosition: { x, y } });
        return value;
    }

    moveNavPoint(id: string, { x, y }: { x: number; y: number }) {
        const value = this.physicalRepository.updateNavPointLocation(id, { x, y });
        this.notifySubscribers({ type: "nav-point-moved", id, newPosition: { x, y } });
        return value;
    }

    addNavPoint(floorId: string, { x, y }: { x: number; y: number }, name: string) {
        const navPoint: NavPoint = {
            id: name,
            location: { x, y },
            floorId,
        };
        this.physicalRepository.addNavPoint(navPoint);
        this.notifySubscribers({ type: "nav-point-added", navPoint });
    }

    getNavPointById(id: string) {
        return this.physicalRepository.navPoints.find((p) => p.id === id);
    }

    deleteNavPoint(id: string) {
        this.physicalRepository.deleteNavPoint(id);
        this.notifySubscribers({ type: "nav-point-deleted", id });
    }

    rotateSeat(roomId: string, seatId: string, theta: number) {
        const value = this.physicalRepository.updateSeatTheta(roomId, seatId, theta);
        this.notifySubscribers({ type: "seat-rotated", roomId, seatId, newTheta: theta });
        return value;
    }

    removeSeat(roomId: string, seatId: string) {
        const value = this.physicalRepository.removeSeatFromRoom(roomId, seatId);
        this.notifySubscribers({ type: "seat-removed", roomId, seatId });
        return value;
    }

    editAmenities(roomId: string, amenities: Amenity[]) {
        const value = this.physicalRepository.updateRoomAmenities(roomId, amenities);
        this.notifySubscribers({ type: "amenities-updated", roomId, amenities });
        return value;
    }

    changeNumberOfSeats(roomId: string, seats: number): void {
        this.physicalRepository.updateNumberOfSeats(roomId, seats);
        this.notifySubscribers({ type: "number-of-seats-updated", roomId, seats });
    }

    changeRoomType(roomId: string, roomType: RoomType): void {
        this.physicalRepository.updateRoomType(roomId, roomType);
        this.notifySubscribers({ type: "room-type-updated", roomId, roomType });
    }

    updatePerson(person: Person): void {
        this.orgRepository.updateEmployee(person);
        this.notifySubscribers({ type: "org" });
    }

    addPerson(person: Omit<Person, "id">): Person {
        const value = this.orgRepository.addEmployee(person);
        this.notifySubscribers({ type: "org" });

        return value;
    }

    deletePerson(person: Person) {
        this.orgRepository.deleteEmployee(person);
        this.notifySubscribers({ type: "org" });
    }

    updateTeam(team: Team): void {
        this.orgRepository.updateTeam(team);
        this.notifySubscribers({ type: "org" });
    }

    addTeam(team: Omit<Team, "id">): Team {
        const value = this.orgRepository.addTeam(team);
        this.notifySubscribers({ type: "org" });

        return value;
    }

    deleteTeam(team: Team) {
        this.orgRepository.deleteTeam(team);
        this.notifySubscribers({ type: "org" });
    }

    updateDepartment(department: Department): void {
        this.orgRepository.updateDepartment(department);
        this.notifySubscribers({ type: "org" });
    }

    addDepartment(department: Omit<Department, "id">): Department {
        const value = this.orgRepository.addDepartment(department);
        this.notifySubscribers({ type: "org" });

        return value;
    }

    deleteDepartment(department: Department) {
        this.orgRepository.deleteDepartment(department);
        this.notifySubscribers({ type: "org" });
    }

    getMyLocation(): { floorId: string; roomId: string } | null {
        const person = this.orgRepository.findEmployeeFromAuth();
        if (!person) return null;

        const seatDetails = this.physicalRepository.getSeatDetailsByEmployeeId(person.id);
        return seatDetails ? { floorId: seatDetails.floorId, roomId: seatDetails.roomId } : null;
    }

    getMyEmployeeId(): string | null {
        return this.orgRepository.findEmployeeFromAuth()?.id ?? null;
    }

    getFloorUtilities(floorId: string) {
        return this.physicalRepository.getFloorUtilities(floorId);
    }

    addUtility(type: OfficeUtilityType, floorId: string, location: { x: number; y: number }): string {
        const id = this.physicalRepository.addUtility(type, floorId, location);
        this.notifySubscribers({ type: "utility-added", id });
        return id;
    }

    removeUtility(id: string): void {
        this.physicalRepository.removeUtility(id);
        this.notifySubscribers({ type: "utility-removed", id });
    }

    moveUtility(id: string, location: { x: number; y: number }): void {
        this.physicalRepository.moveUtility(id, location);
        this.notifySubscribers({ type: "utility-moved", id, location });
    }

    moveRoomLabel(roomId: string, position: { x: number; y: number }): void {
        this.physicalRepository.updateRoomLabelPosition(roomId, position);
        this.notifySubscribers({ type: "room-label-moved", roomId, position });
    }

    getUtilityById(id: string) {
        return this.physicalRepository.getUtilityById(id);
    }
}

export const storageService = new StorageService();
